import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { STATUS_LABELS, STATUS_COLORS, CATEGORY_LABELS } from "@/lib/constants";
import { Bell, Briefcase, Truck, Plus, ArrowRight, DollarSign, Gift, Coins, Search, Clock } from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const mode = user?.active_mode === "driver" ? "driver" : "customer";
  const [myJobs, setMyJobs] = useState([]);
  const [driverJobs, setDriverJobs] = useState([]);
  const [notifs, setNotifs] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [refStats, setRefStats] = useState(null);

  useEffect(() => {
    api.get("/jobs", { params: { mine: true } }).then(r => setMyJobs(r.data));
    api.get("/jobs/driver-jobs").then(r => setDriverJobs(r.data)).catch(() => {});
    api.get("/notifications").then(r => setNotifs(r.data));
    api.get("/payouts").then(r => setPayouts(r.data));
    api.get("/referrals/me").then(r => setRefStats(r.data)).catch(() => {});
  }, []);

  const earnings = payouts.filter(p => p.status === "paid").reduce((s, p) => s + p.amount, 0);
  const pendingEarnings = payouts.filter(p => p.status === "pending").reduce((s, p) => s + p.amount, 0);
  const credit = refStats?.credit_balance || 0;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 relative">
      <div className="absolute top-0 right-0 w-72 h-72 bg-primary/8 rounded-full blur-3xl -z-10 pointer-events-none animate-float" />

      <motion.div
        initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
        className="flex items-center justify-between mb-8 flex-wrap gap-4"
      >
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold">Hej, {user?.full_name?.split(" ")[0] || "Grabber"} <span className="inline-block animate-float">👋</span></h1>
          <p className="text-muted-foreground mt-1">{mode === "driver" ? "Chauffør-overblik, dine opgaver og indtægter" : "Velkommen tilbage, her er din oversigt"}</p>
        </div>
        {mode === "driver" ? (
          <Link to="/jobs"><Button size="lg" className="gap-2 shine-hover rounded-xl" data-testid="find-jobs-btn"><Search className="w-4 h-4" /> Find jobs</Button></Link>
        ) : (
          <Link to="/post"><Button size="lg" className="gap-2 shine-hover rounded-xl" data-testid="post-job-btn"><Plus className="w-4 h-4" /> Opret nyt job</Button></Link>
        )}
      </motion.div>

      {refStats && (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1, duration: 0.5 }}>
          <Card className="mb-6 relative overflow-hidden border-primary/20 bg-gradient-to-br from-primary/5 via-background to-amber-50/40 lift">
            <div className="absolute -top-10 -right-10 w-32 h-32 bg-primary/15 rounded-full blur-2xl pointer-events-none" />
            <CardContent className="py-5 flex flex-wrap items-center gap-4 justify-between relative">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-2xl bg-primary text-primary-foreground flex items-center justify-center shadow-md shadow-primary/30">
                  {credit > 0 ? <Coins className="w-6 h-6" /> : <Gift className="w-6 h-6" />}
                </div>
                <div>
                  <p className="text-xs uppercase tracking-widest text-muted-foreground">{credit > 0 ? "Din saldo" : "Inviter & tjen"}</p>
                  {credit > 0 ? (
                    <p className="text-2xl font-bold text-primary" data-testid="dashboard-credit">{credit.toFixed(2)} kr.</p>
                  ) : (
                    <p className="text-base font-semibold">Få <span className="text-primary">50 kr.</span> for hver ven der fuldfører et job</p>
                  )}
                </div>
              </div>
              <Link to="/referrals" data-testid="referrals-link">
                <Button variant="outline" className="rounded-xl gap-2 border-primary/30">
                  {credit > 0 ? "Se detaljer" : "Hent din kode"} <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </CardContent>
          </Card>
        </motion.div>
      )}

      <motion.div
        initial="hidden" animate="show"
        variants={{ show: { transition: { staggerChildren: 0.06 } } }}
        className={`grid sm:grid-cols-2 ${mode === "driver" ? "lg:grid-cols-4" : "lg:grid-cols-3"} gap-4 mb-8`}
      >
        {mode === "driver" ? (
          <>
            <Stat to="/my-jobs?tab=hauled" label="Hentede jobs" value={driverJobs.length} icon={<Truck />} color="bg-emerald-50 text-emerald-700" testId="stat-hauled" />
            <Stat to="/earnings" label="Optjent (udbetalt)" value={`${earnings} kr.`} icon={<DollarSign />} color="bg-amber-50 text-amber-700" testId="stat-earnings" />
            <Stat to="/earnings" label="Afventer udbetaling" value={`${pendingEarnings} kr.`} icon={<Clock />} color="bg-blue-50 text-blue-700" testId="stat-pending" />
            <Stat to="/dashboard#notifications" label="Notifikationer" value={notifs.filter(n => !n.is_read).length} icon={<Bell />} color="bg-rose-50 text-rose-700" testId="stat-notifs" />
          </>
        ) : (
          <>
            <Stat to="/my-postings" label="Mine opslag" value={myJobs.length} icon={<Briefcase />} color="bg-blue-50 text-blue-700" testId="stat-posted" />
            <Stat to="/my-postings" label="Aktive opslag" value={myJobs.filter(j => !["completed", "cancelled"].includes(j.status)).length} icon={<Clock />} color="bg-emerald-50 text-emerald-700" testId="stat-active" />
            <Stat to="/dashboard#notifications" label="Notifikationer" value={notifs.filter(n => !n.is_read).length} icon={<Bell />} color="bg-rose-50 text-rose-700" testId="stat-notifs" />
          </>
        )}
      </motion.div>

      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg">{mode === "driver" ? "Mine aktive opgaver" : "Mine seneste opslag"}</CardTitle>
            <Link to={mode === "driver" ? "/my-jobs" : "/my-postings"} className="text-sm text-primary hover:underline">Se alle</Link>
          </CardHeader>
          <CardContent>
            {mode === "driver" ? (
              driverJobs.length === 0 ? (
                <div className="text-center py-12">
                  <Truck className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" />
                  <p className="text-muted-foreground mb-4">Du har ingen opgaver endnu, byd på et job!</p>
                  <Link to="/jobs"><Button className="gap-2"><Search className="w-4 h-4" /> Find jobs</Button></Link>
                </div>
              ) : (
                <div className="space-y-2">
                  {driverJobs.slice(0, 5).map(j => <JobRow key={j.id} job={j} />)}
                </div>
              )
            ) : myJobs.length === 0 ? (
              <div className="text-center py-12">
                <Briefcase className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" />
                <p className="text-muted-foreground mb-4">Du har endnu ikke oprettet et job</p>
                <Link to="/post"><Button>Opret dit første job</Button></Link>
              </div>
            ) : (
              <div className="space-y-2">
                {myJobs.slice(0, 5).map(j => <JobRow key={j.id} job={j} />)}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-lg flex items-center gap-2"><Bell className="w-4 h-4" /> Notifikationer</CardTitle></CardHeader>
          <CardContent>
            {notifs.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6">Ingen notifikationer endnu</p>
            ) : (
              <div className="space-y-3">
                {notifs.slice(0, 6).map(n => (
                  <Link to={n.job_id ? `/jobs/${n.job_id}` : "/dashboard"} key={n.id} className={`block p-3 rounded-lg border transition-all hover:border-primary/40 ${n.is_read ? "border-border" : "border-primary/30 bg-primary/5"}`}>
                    <p className="font-medium text-sm">{n.title}</p>
                    <p className="text-xs text-muted-foreground line-clamp-2">{n.message}</p>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {mode === "driver" && payouts.length > 0 && (
        <Card className="mt-6">
          <CardHeader><CardTitle className="text-lg">Mine udbetalinger</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2">
              {payouts.slice(0, 5).map(p => (
                <div key={p.id} className="flex items-center justify-between p-3 rounded-lg border border-border">
                  <div>
                    <p className="font-medium text-sm">Job #{p.job_id.slice(0,8)}</p>
                    <p className="text-xs text-muted-foreground">{new Date(p.created_at).toLocaleDateString("da-DK")}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-semibold">{p.amount} kr.</span>
                    <Badge variant={p.status === "paid" ? "default" : "outline"}>{p.status === "paid" ? "Betalt" : "Afventer"}</Badge>
                  </div>
                </div>
              ))}
            </div>
            {pendingEarnings > 0 && (
              <p className="text-xs text-muted-foreground mt-3">Afventende udbetalinger: <span className="font-semibold text-foreground">{pendingEarnings} kr.</span></p>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function Stat({ label, value, icon, color, to, testId }) {
  const card = (
    <Card className="p-5 lift cursor-pointer h-full group">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${color} [&>svg]:w-5 [&>svg]:h-5 group-hover:scale-110 transition-transform`}>{icon}</div>
      <p className="text-2xl font-bold">{value}</p>
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">{label}</p>
        <ArrowRight className="w-3.5 h-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 -translate-x-1 group-hover:translate-x-0 transition-all" />
      </div>
    </Card>
  );
  return (
    <motion.div variants={{ hidden: { opacity: 0, y: 12 }, show: { opacity: 1, y: 0 } }} data-testid={testId}>
      {to ? <Link to={to}>{card}</Link> : card}
    </motion.div>
  );
}

function JobRow({ job }) {
  return (
    <Link to={`/jobs/${job.id}`} className="flex items-center justify-between p-3 rounded-lg hover:bg-muted transition-colors group">
      <div className="min-w-0">
        <p className="font-medium truncate">{job.title}</p>
        <p className="text-xs text-muted-foreground">{CATEGORY_LABELS[job.category]} · {job.pickup_address}</p>
      </div>
      <div className="flex items-center gap-3 shrink-0">
        <Badge variant="outline" className={`${STATUS_COLORS[job.status]} text-xs`}>{STATUS_LABELS[job.status]}</Badge>
        <span className="font-bold text-primary">{job.budget} DKK</span>
        <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-foreground transition-colors" />
      </div>
    </Link>
  );
}
